import {
  NWS_COUNTY_ZONE,
  NWS_USER_AGENT,
  NWS_ZONE,
  WPC_QPF_API,
  WPC_QPF_FALLBACK_IMAGES,
  WEATHER_LAT,
  WEATHER_LON,
} from '../constants/weather';
import { propertyLocation } from '../constants/propertyLocation';
import { parseWpcQpfMapUrls, type NwsPeriod, type WpcMapEntry } from '../utils/nwsForecast';
import { resolveNwsProductUrl } from '../utils/nwsAreaDiscussion';
import { wpcQpfFullSizeImageUrl } from '../utils/wpcQpfImageUrl';

export interface NwsAlert {
  event: string;
  headline: string;
  severity: string;
  urgency: string;
}

export interface NwsAreaDiscussion {
  productText: string;
  issuanceTime: string;
  office: string;
  zone: string;
}

export interface NwsForecastBundle {
  city: string;
  forecastPeriods: NwsPeriod[];
  hourlyPeriods: NwsPeriod[];
  alerts: NwsAlert[];
  wpcMaps: WpcMapEntry[];
  areaDiscussion: NwsAreaDiscussion | null;
}

async function nwsFetch<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    headers: { Accept: 'application/geo+json', 'User-Agent': NWS_USER_AGENT },
  });
  if (!res.ok) throw new Error(`NWS ${res.status}: ${url}`);
  return res.json() as Promise<T>;
}

interface PointsResponse {
  properties: {
    forecast: string;
    forecastHourly: string;
    relativeLocation?: { properties?: { city?: string } };
  };
}

interface ForecastResponse {
  properties: { periods: NwsPeriod[] };
}

interface AlertsResponse {
  features: Array<{ properties: NwsAlert }>;
}

interface AfdProductListResponse {
  '@graph': Array<{ id: string; '@id'?: string }>;
}

interface AfdProductResponse {
  productText: string;
  issuanceTime: string;
  issuingOffice: string;
}

export async function fetchLatestAreaDiscussion(office: string): Promise<NwsAreaDiscussion | null> {
  try {
    const list = await nwsFetch<AfdProductListResponse>(
      `https://api.weather.gov/products/types/AFD/locations/${office}`
    );
    const latest = list['@graph']?.[0];
    const productUrl = latest ? resolveNwsProductUrl(latest) : null;
    if (!productUrl) return null;

    const product = await nwsFetch<AfdProductResponse>(productUrl);
    const text = product.productText?.trim();
    if (!text) return null;

    return {
      productText: text,
      issuanceTime: product.issuanceTime ?? '',
      office: product.issuingOffice?.replace(/^K?/, '') ?? office,
      zone: NWS_ZONE,
    };
  } catch {
    return null;
  }
}

export async function fetchNwsForecastBundle(): Promise<NwsForecastBundle> {
  const points = await nwsFetch<PointsResponse>(
    `https://api.weather.gov/points/${WEATHER_LAT},${WEATHER_LON}`
  );
  const city = points.properties.relativeLocation?.properties?.city ?? 'Dripping Springs';

  const [forecast, hourly, zoneAlerts, countyAlerts, wpcMaps, areaDiscussion] = await Promise.all([
    nwsFetch<ForecastResponse>(points.properties.forecast),
    nwsFetch<ForecastResponse>(points.properties.forecastHourly),
    nwsFetch<AlertsResponse>(`https://api.weather.gov/alerts/active?zone=${NWS_ZONE}`).catch(() => ({
      features: [],
    })),
    nwsFetch<AlertsResponse>(`https://api.weather.gov/alerts/active?zone=${NWS_COUNTY_ZONE}`).catch(() => ({
      features: [],
    })),
    loadWpcMaps(),
    fetchLatestAreaDiscussion(propertyLocation.nws.office),
  ]);

  const alertMap = new Map<string, NwsAlert>();
  for (const f of [...zoneAlerts.features, ...countyAlerts.features]) {
    const a = f.properties;
    if (a?.event) alertMap.set(`${a.event}:${a.headline}`, a);
  }

  return {
    city,
    forecastPeriods: forecast.properties.periods,
    hourlyPeriods: hourly.properties.periods,
    alerts: [...alertMap.values()],
    wpcMaps,
    areaDiscussion,
  };
}

async function loadWpcMaps(): Promise<WpcMapEntry[]> {
  try {
    const res = await fetch(WPC_QPF_API);
    if (!res.ok) throw new Error(String(res.status));
    const html = await res.text();
    const parsed = parseWpcQpfMapUrls(html).map(m => ({
      ...m,
      url: wpcQpfFullSizeImageUrl(m.url),
    }));
    if (parsed.length >= 2) return parsed;
  } catch {
    /* WPC API often blocks browser CORS — use stable fallback image URLs */
  }
  return [...WPC_QPF_FALLBACK_IMAGES];
}
